import * as THREE from 'three';
import {
  getPortalOutlinePoint,
  getPortalVisualKind,
  PORTAL_SURFACE_VARIANTS,
} from './portalProfiles';

export const portalVertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = `
  varying vec2 vUv;
  uniform float time;
  uniform float locked;
  uniform float charge;
  uniform float passage;
  uniform float variant;
  uniform vec3 tint;
  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float band(float distance, float width) { return 1.0 - smoothstep(0.0, width, abs(distance)); }
  void main() {
    vec2 p = vUv * 2.0 - 1.0;
    float originalRadius = length(p);
    p.x += sin(p.y * 24.0 + time * 8.0) * locked * 0.018;
    float radius = length(p);
    float angle = atan(p.y, p.x);
    float rim = pow(min(radius, 1.0), 6.0);
    float wave = sin(angle * 7.0 - radius * 17.0 + time * 1.4);
    float rings = pow(0.5 + 0.5 * sin(radius * 32.0 - angle * 3.0 - time * 2.0), 7.0);
    float grid = step(0.94, abs(sin(p.x * 26.0 + wave * 0.22)))
               + step(0.96, abs(sin(p.y * 26.0 + time * 0.18)));
    vec3 color = tint * (0.09 + rings * 0.3 + grid * 0.08 + rim * 0.8);

    if (variant > 0.5 && variant < 1.5) {
      // Supply: packets rise through a rectilinear loading field.
      vec2 cell = vec2(p.x * 6.0, p.y * 5.0 - time * 0.65);
      vec2 edge = abs(fract(cell) - 0.5);
      float lanes = step(0.465, edge.x) * 0.2 + step(0.475, edge.y) * 0.12;
      float packet = step(0.74, hash(floor(cell))) * (1.0 - step(0.27, max(edge.x, edge.y)));
      color = tint * (0.055 + lanes + packet * 0.38 + rim * 0.55);
      color += vec3(0.22, 0.65, 0.65) * band(fract(p.y * 0.5 - time * 0.22) - 0.5, 0.035) * 0.25;
    } else if (variant < 2.5 && variant > 1.5) {
      // Treasure: nested vault seals and warm points of suspended gold.
      float hexagon = max(abs(p.x) * 0.866 + abs(p.y) * 0.5, abs(p.y));
      float seals = pow(0.5 + 0.5 * cos(hexagon * 40.0 - time * 0.75), 20.0);
      vec2 cell = floor(p * 24.0);
      float spark = pow(hash(cell), 55.0) * pow(0.5 + 0.5 * sin(time * 1.4 + hash(cell) * 20.0), 4.0);
      color = tint * (0.045 + seals * 0.32 + rim * 0.65 + spark * 1.3);
      color += vec3(0.6, 0.25, 0.04) * pow(1.0 - min(radius, 1.0), 3.0) * 0.18;
    } else if (variant < 3.5 && variant > 2.5) {
      // Recovery: breathing fluid ripples crossed by a single living pulse.
      float fluid = pow(0.5 + 0.5 * sin(radius * 24.0 - time * 1.5 + sin(angle * 3.0) * 1.4), 10.0);
      float heartbeat = exp(-pow((p.x + 0.06) * 19.0, 2.0)) * 0.36 - exp(-pow((p.x - 0.07) * 22.0, 2.0)) * 0.19;
      float pulse = band(p.y - heartbeat, 0.022) * (0.28 + pow(0.5 + 0.5 * sin(p.x * 3.0 - time * 2.0), 8.0) * 0.6);
      color = tint * (0.04 + fluid * 0.19 + rim * 0.6) + vec3(0.6, 1.0, 0.88) * pulse;
    } else if (variant < 4.5 && variant > 3.5) {
      // Sanctuary: a quiet aurora with small fixed stars.
      float curtain = pow(0.5 + 0.5 * sin(p.x * 12.0 + sin(p.y * 4.0 + time * 0.3) * 2.0), 5.0);
      float mist = curtain * smoothstep(-0.9, 0.6, p.y) * 0.38;
      float stars = pow(hash(floor(p * 34.0)), 85.0) * 0.6;
      color = tint * (0.035 + mist + rim * 0.45) + vec3(0.55, 0.5, 1.0) * (mist * 0.3 + stars);
    } else if (variant < 5.5 && variant > 4.5) {
      // Trial: moving diamond chevrons and a blade of heat down the center.
      float diamond = abs(p.x) + abs(p.y);
      float chevrons = pow(0.5 + 0.5 * sin(diamond * 25.0 + time * 2.0), 18.0);
      float heat = exp(-abs(p.x) * 22.0) * (0.15 + pow(0.5 + 0.5 * sin(p.y * 13.0 - time * 3.0), 4.0) * 0.5);
      color = tint * (0.05 + chevrons * 0.3 + rim * 0.5) + vec3(1.0, 0.45, 0.12) * heat;
    } else if (variant < 6.5 && variant > 5.5) {
      // Anomaly: gravitational lensing around a dark, displaced singularity.
      float slice = floor(p.y * 13.0);
      float fault = sin(slice * 8.1 + time * 0.65);
      p.x += step(0.76, fault) * sin(time * 1.6 + slice) * (0.04 + locked * 0.1);
      p -= vec2(sin(time * 0.47) * 0.07, cos(time * 0.33) * 0.045);
      radius = length(p);
      angle = atan(p.y, p.x);
      float lens = angle * 3.0 + log(max(radius, 0.035)) * 8.0 - time * 1.25;
      float filament = pow(0.5 + 0.5 * sin(lens + sin(angle * 5.0 - time * 0.7)), 14.0);
      float second = pow(0.5 + 0.5 * sin(lens * 1.08 + 2.0), 22.0);
      float horizon = band(radius - 0.24 - sin(angle * 3.0 + time * 0.4) * 0.015, 0.052);
      float tear = band(p.x + sin(p.y * 7.0 + time * 0.65) * 0.06, 0.009);
      float voidMask = smoothstep(0.17, 0.3, radius);
      vec3 spectral = mix(vec3(0.25, 0.85, 1.0), vec3(0.92, 0.18, 0.9), 0.5 + sin(angle * 2.0 + time * 0.4) * 0.5);
      color = (tint * 0.025 + spectral * filament * 0.5 + vec3(0.15, 0.8, 1.0) * second * 0.25) * voidMask;
      color += spectral * horizon * 0.85 + vec3(0.55, 0.25, 1.0) * tear * voidMask * 0.4;
      color += mix(vec3(0.2, 0.7, 1.0), vec3(0.9, 0.14, 0.75), sin(angle * 3.0) * 0.5 + 0.5) * rim * 0.55;
    } else if (variant < 7.5 && variant > 6.5) {
      // Relic: facets refract different colors across a crystalline interior.
      vec2 facets = vec2(p.x + p.y * 0.55, p.x - p.y * 0.55) * 7.0;
      vec2 edge = abs(fract(facets) - 0.5);
      float seams = pow(max(edge.x, edge.y) * 2.0, 22.0);
      float prism = hash(floor(facets));
      color = mix(tint, vec3(0.25, 0.9, 1.0), prism * 0.6) * (0.055 + seams * 0.3 + rim * 0.5);
      color += tint * pow(0.5 + 0.5 * sin(radius * 13.0 - time), 10.0) * 0.2;
    } else if (variant > 9.5) {
      // Warden: broken siege seals and a molten fault through armored glass.
      float seals = band(sin(angle * 4.0 + radius * 8.0), 0.1) * rim;
      float fault = band(p.x + sin(p.y * 11.0 + time * 0.9) * 0.1, 0.045);
      float heat = pow(0.5 + 0.5 * sin(radius * 35.0 - time * 3.5), 14.0);
      color = tint * (0.04 + seals * 0.75 + heat * 0.3 + rim * 0.5);
      color += vec3(1.0, 0.2, 0.22) * fault * 0.7;
    } else if (variant > 8.5) {
      // Core: a dark reactor iris, with accretion filaments feeding its heart.
      float warp = angle + radius * 3.0 - time * 0.23;
      float filaments = pow(0.5 + 0.5 * sin(warp * 9.0 - radius * 13.0), 16.0);
      float horizon = band(radius - 0.29 - sin(angle * 6.0 + time * 0.7) * 0.012, 0.035);
      float iris = smoothstep(0.22, 0.36, radius);
      float circuit = pow(0.5 + 0.5 * cos(radius * 46.0 + angle * 6.0 + time * 0.8), 26.0);
      vec3 heat = mix(vec3(0.48, 0.18, 1.0), vec3(1.0, 0.22, 0.38), 0.5 + 0.5 * sin(warp * 2.0));
      color = (heat * filaments * 0.43 + tint * circuit * 0.17 + tint * rim * 0.65) * iris;
      color += vec3(1.0, 0.55, 0.67) * horizon * 0.95;
      color += vec3(0.8, 0.9, 1.0) * band(p.x, 0.006) * exp(-radius * 5.0) * 0.4;
    } else if (variant > 7.5) {
      float scan = pow(0.5 + 0.5 * sin(p.y * 45.0 + time * 1.4), 18.0);
      float code = step(0.82, hash(floor(vec2(p.x * 14.0, p.y * 18.0 - time * 0.35))));
      color = tint * (0.045 + scan * 0.13 + code * 0.18 + rim * 0.55);
      color += vec3(0.9, 0.44, 0.12) * band(fract((p.x + p.y) * 2.5) - 0.5, 0.04) * rim * 0.3;
    }
    float instability = step(0.79, sin(floor(vUv.y * 18.0) * 13.7 + time * 2.0));
    color *= mix(1.0, 0.42 + instability * 0.35, locked);
    color += tint * charge * (rings * 0.28 + pow(max(0.0, 1.0 - radius), 3.0) * 0.4);
    gl_FragColor = vec4(color + vec3(0.012, 0.018, 0.035), 1.0 - smoothstep(0.97, 1.0, originalRadius));
    gl_FragColor.a *= 1.0 - passage;
  }
`;

export const createPortalSurface = (route, index = 0) => {
  const kind = getPortalVisualKind(route);
  const geometry = new THREE.CircleGeometry(1, 96);
  const positions = geometry.attributes.position;
  for (let i = 1; i < positions.count; i += 1) {
    const angle = Math.atan2(positions.getY(i), positions.getX(i));
    const point = getPortalOutlinePoint(kind, angle);
    positions.setXY(i, point.x, point.y);
  }
  geometry.computeBoundingSphere();
  const aperture = new THREE.Mesh(
    geometry,
    new THREE.ShaderMaterial({
      vertexShader: portalVertexShader,
      fragmentShader,
      uniforms: {
        time: { value: index * 4 },
        locked: { value: route.locked ? 1 : 0 },
        charge: { value: 0 },
        passage: { value: 0 },
        variant: { value: PORTAL_SURFACE_VARIANTS[kind] || 0 },
        tint: { value: new THREE.Color(route.color || '#79e9ff') },
      },
      transparent: true,
      side: THREE.DoubleSide,
    })
  );
  aperture.scale.set(1.18, 1.78, 1);
  aperture.userData.route = route;
  return aperture;
};
